/**
 * 统一封装的后端接口请求工具
 *
 * 统一处理以下逻辑：
 *
 * 1. SET 化参数
 * 2. 人机识别 token
 * 3. 特定接口强制走中心 SET
 * 4. HTTP成功率、业务成功率上报
 * 5. 错误码统一处理
 * 6. Logan 日志
 */

import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'; // eslint-disable-line waimaie/no-direct-import-axios
import qs from 'qs';

import setParamsRequestInterceptor from './interceptors/request/setParams';
import coreApiMonitorResponseInterceptor from './interceptors/response/coreApiMonitor'; // eslint-disable-line import/no-cycle
import loganRequestInterceptor from './interceptors/request/logan';
import loganResponseInterceptor from './interceptors/response/logan'; // eslint-disable-line import/no-cycle
import businessCodeInterceptor from './interceptors/response/businessCode'; // eslint-disable-line import/no-cycle
import rohrRequestInterceptor from './interceptors/request/rohr';

export interface IServerResponse<Data> {
    code: number;
    msg?: string;
    message?: string;
    data: Data;
}

// [请求拦截器]
axios.interceptors.request.use(setParamsRequestInterceptor.onFulfilled);
axios.interceptors.request.use(loganRequestInterceptor.onFulfilled);
axios.interceptors.request.use(rohrRequestInterceptor.onFulfilled);

// [响应拦截器]
axios.interceptors.response.use(
    loganResponseInterceptor.onFulfilled,
    loganResponseInterceptor.onRejected,
);
axios.interceptors.response.use(
    coreApiMonitorResponseInterceptor.onFulfilled,
    coreApiMonitorResponseInterceptor.onRejected,
);
axios.interceptors.response.use(businessCodeInterceptor.onFulfilled);

/**
 * 检查业务状态码是否为 0
 *
 * @param response Axios 响应体实例
 */
const responseHandler = (response: AxiosResponse<IServerResponse<any>>) => {
    if (response.data.code === 0) {
        return response.data.data;
    }

    if ((response.data as any) === '') {
        // 识别食亨异常场景
        // eslint-disable-next-line no-throw-literal
        throw { message: '', isEmptyBody: true };
    }

    throw Error(response.data.msg || response.data.message);
};

type WrappedAxiosError = {
    message: string;
    isNetworkError?: boolean;
    isNetworkAbort?: boolean;
};

/** 识别XMLHttpRequest的error与abort，区分HTTP 4xx，5xx */
const detectXHRErrorAndAbort = (error: any) => {
    const wrappedError: WrappedAxiosError = { message: '网络异常，请稍后再试' };
    if (error && error.message && error.message === 'Network Error') {
        wrappedError.isNetworkError = true;
    }
    if (error && error.message && error.message === 'Request aborted') {
        wrappedError.isNetworkAbort = true;
    }
    throw wrappedError;
};

/**
 * 发送一个 GET 请求，并统一封装数据字段解析逻辑
 *
 * @param url 请求的 URL
 * @param config 配置对象，具体参数含义可以参考 [axios 文档](https://github.com/axios/axios#request-config)
 *
 */
const get = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return axios.get<IServerResponse<T>>(url, config).then(responseHandler, detectXHRErrorAndAbort);
};

/**
 * 发送一个 POST 请求，并统一封装数据字段解析逻辑
 *
 * 与 `get` 方法进行了相同的接口数据格式约定，详情请参考 `get` 方法文档。
 *
 * @param url 请求的 URL
 * @param data 发送到服务端的数据
 * @param config 配置对象，具体参数含义可以参考 [axios 文档](https://github.com/axios/axios#request-config)
 *
 */
const post = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return axios
        .post<IServerResponse<T>>(url, data, config)
        .then(responseHandler, detectXHRErrorAndAbort);
};

const rawGet = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return axios.get<T>(url, config).then(r => r.data, detectXHRErrorAndAbort);
};

const rawPost = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return axios.post<T>(url, data, config).then(r => r.data, detectXHRErrorAndAbort);
};

/* jscpd:ignore-start */
const rawPostAsFormSubmit = <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
): Promise<T> => {
    if (!config) {
        config = {};
    }
    if (config.headers) {
        config.headers['content-type'] = 'application/x-www-form-urlencoded';
    } else {
        config.headers = { 'content-type': 'application/x-www-form-urlencoded' };
    }
    return axios.post<T>(url, qs.stringify(data), config).then(r => r.data, detectXHRErrorAndAbort);
};
/* jscpd:ignore-end */

const postAsFormSubmit = <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
): Promise<T> => {
    if (!config) {
        config = {};
    }
    if (config.headers) {
        config.headers['content-type'] = 'application/x-www-form-urlencoded';
    } else {
        config.headers = { 'content-type': 'application/x-www-form-urlencoded' };
    }
    return post<T>(url, qs.stringify(data), config);
};

const wrapper = {
    /**
     * 发送一个 GET 请求，并统一封装数据字段解析逻辑
     *
     * **注意：调用此方法拿到的是后端接口返回数据中的 data 字段部分的内容，若还需要访问 `code`, `message` 字段，请使用 axiosWrapper#rawGet 方法**
     *
     * ### 约定
     *
     * 商家中心接口都遵循以下数据格式：
     *
     * ```json
     * {
     *    code: number,
     *    message: string,
     *    data: {...}
     * }
     * ```
     *
     * 调用此方法是只需给出 data 字段的类型定义(泛型 T)，即可在函数返回的 Promise 中取得对应类型的数据。
     *
     * ### 示例：
     *
     * 假设我们有数据类型：
     *
     * ```ts
     * interface IUser {
     *  name: string;
     *  age: number;
     * }
     * ```
     *
     * 服务端接口 `/users/:id` 返回结果为：
     *
     * ```json
     * {
     *  code: 0,
     *  message: 'success',
     *  data: {...}
     * }
     * ```
     *
     * 我们就可以像如下一样调用接口：
     *
     * ```ts
     * get<IUser>('/users/1').then(user => {
     *  // user 对象即为 IUser 类型
     * })
     * ```
     *
     * @param url 请求的 URL
     * @param config 配置对象，具体参数含义可以参考 [axios 文档](https://github.com/axios/axios#request-config)
     *
     */
    get,
    /**
     * 发送一个 POST 请求，并统一封装数据字段解析逻辑
     *
     * **注意：调用此方法拿到的是后端接口返回数据中的 data 字段部分的内容，若还需要访问 `code`, `message` 字段，请使用 axiosWrapper#rawPost 方法**
     *
     * 与 `get` 方法进行了相同的接口数据格式约定，详情请参考 `get` 方法文档。
     *
     * @param url 请求的 URL
     * @param data 发送到服务端的数据
     * @param config 配置对象，具体参数含义可以参考 [axios 文档](https://github.com/axios/axios#request-config)
     *
     */
    post,
    rawGet,
    rawPost,
    /**
     * 以 [application/x-www-form-urlencoded](https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods/POST) 格式发起一个 POST 请求，并统一封装数据字段解析逻辑
     *
     * 与 `get` 方法进行了相同的接口数据格式约定，详情请参考 `get` 方法文档。
     *
     * @param url 请求的 URL
     * @param data 发送到服务端的数据
     *
     */
    postAsFormSubmit,
    rawPostAsFormSubmit,
};

export default wrapper;
